'use client';
import { classNames } from '@lib/classNames/classNames';
import { memo, useState } from 'react';

import { TabsProps } from './types';

export const Tabs: React.FC<TabsProps> = memo(
  ({ tabs, customClassNames, defaultActiveTab = 0, verticalTab }) => {
    const [activeTabIndex, setActiveTabIndex] =
      useState<number>(defaultActiveTab);

    const handleTabClick = (index: number) => {
      setActiveTabIndex(index);
    };

    return (
      <>
        <div
          className={classNames({
            cls: 'max-sm:invisible-scrollbar flex w-full gap-4 overflow-x-auto max-sm:gap-0 max-sm:px-5 max-sm:whitespace-nowrap',
            additional: [customClassNames?.main],
          })}
          role="tablist"
        >
          {tabs.map((tab, index) => {
            const isActive = index === activeTabIndex;

            return (
              <div
                role="tab"
                key={index}
                className={classNames({
                  cls: 'group max-sm:hover:bg-custom-surface-contrast-mid flex flex-1 cursor-pointer items-center justify-center rounded-[12px] px-4 py-3 transition-colors duration-300 max-sm:shrink-0 max-sm:grow max-sm:basis-0 max-sm:py-3',
                  mods: {
                    'bg-surface-base-secondary': !isActive,
                    'bg-[#12951A]': isActive,
                    'hover:bg-custom-surface-contrast-mid': !isActive,
                    'max-sm:bg-transparent max-sm:hover:bg-transparent':
                      !isActive && !verticalTab,
                    'max-sm:bg-surface-base-tertiary max-sm:hover:bg-surface-base-tertiary':
                      isActive && !verticalTab,
                    'flex-col items-start justify-start gap-9 max-sm:mr-3 max-sm:min-w-[144px] max-sm:gap-5':
                      verticalTab,
                    'flex-col-reverse': tab.iconAfterLabel,
                  },
                })}
                onClick={() => handleTabClick(index)}
              >
                {tab.icon && (
                  <div
                    className={classNames({
                      cls: 'mr-2 max-sm:hidden',
                      mods: {
                        [customClassNames?.inactiveIcon ?? '']: !isActive,
                        'max-sm:!block': verticalTab,
                      },
                      additional: [customClassNames?.icon],
                    })}
                  >
                    {tab.icon}
                  </div>
                )}
                <span
                  id={tab.id}
                  className={classNames({
                    cls: 'text-text-base-primary text-[1rem] font-medium max-sm:font-normal',
                    mods: { [customClassNames?.inactiveIcon ?? '']: !isActive },
                  })}
                >
                  {tab.label}
                </span>
              </div>
            );
          })}
        </div>

        <div className="mt-4 w-full max-sm:mt-6">
          {tabs.map((tab, index) => (
            <div
              key={index}
              className={classNames({
                mods: {
                  block: index === activeTabIndex,
                  hidden: index !== activeTabIndex,
                },
              })}
            >
              {tab.content}
            </div>
          ))}
        </div>
      </>
    );
  },
);
